import { Button, Card, Col, Empty, Layout, Row, Spin, Tooltip, Typography } from 'antd'
import { Content } from 'antd/lib/layout/layout'
import React, { useEffect, useMemo, useState } from 'react'
import Sidebar from '../../component/siderbar/sidebar'
import { Header } from '../../component/header/header'
import {
    CameraOutlined,
    CarOutlined,
    ClockCircleOutlined,
    EnvironmentOutlined,
    ExperimentOutlined,
    EyeOutlined,
    FireOutlined,
    SafetyCertificateOutlined,
    ShoppingOutlined,
    UserOutlined,
    // WarningOutlined,
} from '@ant-design/icons'

import {
    DETECTION_TYPES,
    DETECTION_TYPES_FILTER,
    // SEVERITIES,
    TIME_RANGES,
    buildAlerts,
    buildTimeSeries,
    filterDetections,
    latestDetectionDate,
    loadDetections,
    summarise,
    zoneStats,
} from './analyticsData'
import DetectionTrends from './DetectionTrends'
import AlertsPanel from './AlertsPanel'
import './Analytics.css'

const { Title } = Typography;

const TYPE_ICONS = {
    'Face Recognition': <UserOutlined />,
    'Number Plate': <CarOutlined />,
    'Baggage Detection': <ShoppingOutlined />,
    'PPE Detection': <SafetyCertificateOutlined />,
    'FireSmoke Detection': <FireOutlined />,
    'ForeignMaterial Detection': <ExperimentOutlined />,
};

// Selected pills tint themselves with the category colour; unselected stay neutral.
const accentStyle = (color, active) => (active
    ? { color, backgroundColor: `${color}1A`, boxShadow: `inset 0 0 0 1px ${color}59` }
    : {});

const Analytics = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const [selectedTypes, setSelectedTypes] = useState([]);
    const [timeRange, setTimeRange] = useState('all');
    // const [selectedSeverities, setSelectedSeverities] = useState([]);

    useEffect(() => {
        let cancelled = false;
        loadDetections()
            .then((detections) => { if (!cancelled) setRows(detections); })
            .catch((error) => console.error('Failed to load analytics detections:', error))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const filtered = useMemo(
        () => filterDetections(rows, { types: selectedTypes, timeRange }),
        [rows, selectedTypes, timeRange]
    );

    const kpis = useMemo(() => summarise(filtered), [filtered]);
    const zones = useMemo(() => zoneStats(filtered, rows), [filtered, rows]);
    const trends = useMemo(() => buildTimeSeries(filtered, selectedTypes), [filtered, selectedTypes]);
    const latest = useMemo(() => latestDetectionDate(rows), [rows]);
    // Alerts come from the full set, not `filtered` — the filter pills shape the
    // dashboard, they shouldn't make outstanding alerts disappear.
    const alerts = useMemo(() => buildAlerts(rows), [rows]);

    // With no type filter every card is relevant; with one, hide the cards the user filtered out.
    const visibleKpis = selectedTypes.length
        ? kpis.filter((kpi) => selectedTypes.includes(kpi.key))
        : kpis;

    const toggle = (value, list, setList) => {
        setList(list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);
    };

    const filtersActive = selectedTypes.length > 0 || timeRange !== 'all';

    const resetFilters = () => {
        setSelectedTypes([]);
        setTimeRange('all');
    };

    return (
        <div>
            <Layout>
                <Header />
                <Layout>
                    <Sidebar currentKey={"4"} />
                    <Content>
                        <Spin spinning={loading}>
                            <div className='analytics-page'>
                                <div className='analytics-page-head'>
                                    <div>
                                        <Title level={2} className='analytics-heading'>Vidan Analytics Dashboard</Title>
                                        <div className='analytics-subheading'>
                                            AI-powered detection monitoring across all zones
                                        </div>
                                    </div>
                                    <div className='analytics-page-head-right'>
                                        <div className='analytics-updated'>
                                            {latest
                                                ? `Latest detection ${latest.format('Do MMMM, YYYY')}`
                                                : 'No detections recorded'}
                                        </div>
                                        <AlertsPanel alerts={alerts} />
                                    </div>
                                </div>

                                <div className='analytics-filter-bar'>
                                    <div className='analytics-filter-group'>
                                        <Tooltip title='Time range'>
                                            <ClockCircleOutlined className='analytics-filter-icon' />
                                        </Tooltip>
                                        <div className='analytics-pill-set'>
                                            {TIME_RANGES.map((range) => (
                                                <button
                                                    key={range.key}
                                                    type='button'
                                                    className={`analytics-pill ${timeRange === range.key ? 'is-active' : ''}`}
                                                    onClick={() => setTimeRange(range.key)}
                                                >
                                                    {range.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className='analytics-filter-group'>
                                        {/* <Tooltip title='Alert level'>
                                            <WarningOutlined className='analytics-filter-icon' />
                                        </Tooltip>
                                        <div className='analytics-pill-set'>
                                            {SEVERITIES.map((severity) => {
                                                const active = selectedSeverities.includes(severity.key);
                                                return (
                                                    <button
                                                        key={severity.key}
                                                        type='button'
                                                        className={`analytics-pill ${active ? 'is-active-accent' : ''}`}
                                                        style={accentStyle(severity.color, active)}
                                                        onClick={() => toggle(severity.key, selectedSeverities, setSelectedSeverities)}
                                                    >
                                                        {severity.label}
                                                    </button>
                                                );
                                            })}
                                        </div> */}
                                    </div>

                                    <div className='analytics-filter-group'>
                                        <Tooltip title='Detection type'>
                                            <EyeOutlined className='analytics-filter-icon' />
                                        </Tooltip>
                                        <div className='analytics-pill-set'>
                                            {DETECTION_TYPES_FILTER.map((type) => {
                                                const active = selectedTypes.includes(type.key);
                                                return (
                                                    <button
                                                        key={type.key}
                                                        type='button'
                                                        className={`analytics-pill ${active ? 'is-active-accent' : ''}`}
                                                        style={accentStyle(type.color, active)}
                                                        onClick={() => toggle(type.key, selectedTypes, setSelectedTypes)}
                                                    >
                                                        {type.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {filtersActive && (
                                        <Button type='link' className='analytics-filter-reset' onClick={resetFilters}>
                                            Clear filters
                                        </Button>
                                    )}
                                </div>

                                <Row gutter={[16, 16]}>
                                    {visibleKpis.map((kpi) => (
                                        <Col key={kpi.key} xs={24} sm={12} lg={8} xl={4}>
                                            <Card bordered={false} className='analytics-kpi-card'>
                                                <div className='analytics-kpi-head'>
                                                    <div className='analytics-kpi-label'>{kpi.label}</div>
                                                    <div
                                                        className='analytics-kpi-icon'
                                                        style={{ backgroundColor: `${kpi.color}1A`, color: kpi.color }}
                                                    >
                                                        {TYPE_ICONS[kpi.key]}
                                                    </div>
                                                </div>
                                                <div className='analytics-kpi-value'>{kpi.total}</div>
                                                    {kpi.highlightCount!= null && (
                                                        <div className='analytics-kpi-sub'>
                                                            {kpi.highlightCount} of {kpi.total} {kpi.highlightNoun}
                                                        </div>
                                                    )}
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>

                                <Row style={{ marginTop: '16px' }}>
                                    <Col span={24}>
                                        <DetectionTrends
                                            buckets={trends.buckets}
                                            series={trends.series}
                                            granularity={trends.granularity}
                                            max={trends.max}
                                        />
                                    </Col>
                                </Row>

                                <Title level={4} className='analytics-section-title'>Zone Statistics</Title>
                                <Row gutter={[16, 16]}>
                                    {zones.map((zone) => (
                                        <Col key={zone.key} xs={24} sm={12} lg={6}>
                                            <Card bordered={false} className='analytics-zone-card'>
                                                <div className='analytics-zone-name'>
                                                    <EnvironmentOutlined />
                                                    {zone.name}
                                                </div>
                                                <div className='analytics-zone-desc'>{zone.description}</div>
                                                <div className='analytics-zone-meta'>
                                                    <CameraOutlined />
                                                    <Tooltip title={zone.cameras.join(', ') || 'No cameras'}>
                                                        <span>
                                                            {zone.cameraCount} {zone.cameraCount === 1 ? 'camera' : 'cameras'}
                                                        </span>
                                                    </Tooltip>
                                                </div>
                                                <div className='analytics-zone-count'>{zone.detections}</div>
                                                <div className='analytics-zone-count-label'>
                                                    {zone.detections === 1 ? 'detection' : 'detections'} in this zone
                                                </div>
                                                <div className='analytics-zone-types'>
                                                    {zone.activeTypes.length ? zone.activeTypes.map((type) => (
                                                        <Tooltip key={type.key} title={type.label}>
                                                            <div
                                                                className='analytics-zone-type-dot'
                                                                style={{ backgroundColor: `${type.color}1A`, color: type.color }}
                                                            >
                                                                {TYPE_ICONS[type.key]}
                                                            </div>
                                                        </Tooltip>
                                                    )) : (
                                                        <span className='analytics-zone-empty'>
                                                            No detections for these filters
                                                        </span>
                                                    )}
                                                </div>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>

                                {/* {!loading && filtered.length === 0 && (
                                    <Row style={{ marginTop: '16px' }}>
                                        <Col span={24}>
                                            <div className='analytics-empty-state'>
                                                <Empty
                                                    description={
                                                        timeRange !== 'all'
                                                            ? `No detections in this time range. The most recent detection was ${latest ? latest.format('Do MMMM, YYYY') : 'never'}.`
                                                            : 'No detections match these filters.'
                                                    }
                                                >
                                                    {filtersActive && <Button onClick={resetFilters}>Clear filters</Button>}
                                                </Empty>
                                            </div>
                                        </Col>
                                    </Row>
                                )} */}
                            </div>
                        </Spin>
                    </Content>
                </Layout>
            </Layout>
        </div>
    )
}

export default Analytics
